import "../../../App.css";
import {useState, useEffect, useCallback} from "react";
import {
    Button,
    message,
    Layout,
} from "antd";
import "antd/dist/reset.css";
import { useOrderId } from "../../../hooks/UseOrderId";
import { PaymentType } from "../../../App";
import { usePostCheckoutApi, usePostCheckoutChannelApi } from "../../../api";
import rocketLogo from "../../../assets/rocket_logo.png";
import nagadLogo from "../../../assets/nagad_logo.png";
import bkashLogo from "../../../assets/bkash_logo.png";
import copy from "../../../assets/copy.png";
import "./BdWalletModel.css";
import useAxios from "axios-hooks";
import {CheckoutStatusResult} from "../../../types/CheckoutStatusResult";

const { Content } = Layout;

const logoMap = {
    ROCKET: rocketLogo,
    NAGAD: nagadLogo,
    BKASH: bkashLogo
};

const PROVIDER_STORAGE_KEY = 'hambapay_provider_type';
const EXPIRY_STORAGE_KEY = 'hambapay_provider_expiry';
const RECEIVER_ACCOUNT_KEY = 'hambapay_receiver_account';
const ORDER_ID_KEY = 'hambapay_order_id';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const SUBMIT_TIME_KEY = 'hambapay_submit_time';
const OverdueTime = 3 * 60 * 1000;
const PollingTime = 10 * 1000;

function BdWalletMode({ getCheckoutInfoData }: PaymentType) {
    const id = useOrderId();

    // Helper function to clear all cache
    const clearAllCache = () => {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        localStorage.removeItem(EXPIRY_STORAGE_KEY);
        localStorage.removeItem(RECEIVER_ACCOUNT_KEY);
        localStorage.removeItem(ORDER_ID_KEY);
        localStorage.removeItem(SUBMIT_TIME_KEY);
    };

    // Helper function to get cached provider type
    const getCachedProviderType = () => {
        const savedOrderId = localStorage.getItem(ORDER_ID_KEY);
        // 如果是新的訂單ID，清除所有緩存
        if (savedOrderId !== id) {
            clearAllCache();
            return "";
        }

        const savedType = localStorage.getItem(PROVIDER_STORAGE_KEY);
        const expiryTime = localStorage.getItem(EXPIRY_STORAGE_KEY);
        
        if (savedType && expiryTime) {
            const now = new Date().getTime();
            if (now < parseInt(expiryTime)) {
                return savedType;
            } else {
                clearAllCache();
            }
        }
        return "";
    };

    // Helper function to get cached receiver account
    const getCachedReceiverAccount = () => {
        const savedOrderId = localStorage.getItem(ORDER_ID_KEY);
        if (savedOrderId !== id) {
            return '';
        }

        const expiryTime = localStorage.getItem(EXPIRY_STORAGE_KEY);
        if (expiryTime && new Date().getTime() < parseInt(expiryTime)) {
            return localStorage.getItem(RECEIVER_ACCOUNT_KEY) || '';
        }
        return '';
    };

    const [
        {
            data: getCheckoutResultData,
            loading: pollingLoading
        },
        pollingCheckoutResult
    ] = useAxios<CheckoutStatusResult>(`/v2/orders/${id}`);
    const [submitCheckout, setSubmitCheckout] = useState(false);
    const [receiverAccount, setReceiveAccount] = useState<string>(getCachedReceiverAccount());
    const [expiryTime] = useState<string>(getCheckoutInfoData?.expiryTime || '');
    const [isExpired, setIsExpired] = useState(false);
    const [providerType, setProviderType] = useState<string>(getCachedProviderType() || getCheckoutInfoData.receiverBank?.toUpperCase() || "");
    const [selectedChannel, setSelectedChannel] = useState<string>(getCachedProviderType() || getCheckoutInfoData.receiverBank?.toUpperCase() || "");
    const [timeLeft, setTimeLeft] = useState({ hours: '00', minutes: '00', seconds: '00' });
    const [transactionId, setTransactionId] = useState("");
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [methodError, setMethodError] = useState(false);
    const [transactionError, setTransactionError] = useState(false);
    const [submitTime, setSubmitTime] = useState<string>('');
    const [allowSubmit, setAllowSubmit] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [isPollingStopped, setIsPollingStopped] = useState(false);
    const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);

    // NOTICE: ant design
    const [messageApi, contextHolder] = message.useMessage();

    // API
    const { loading, executePost } = usePostCheckoutApi(id);
    const onFormFinish = (values: any) => {
        setMethodError(!providerType);
        setTransactionError(!values.transactionId || values.transactionId.trim() === '');
        setSubmitCheckout(true);

        if (!providerType || !values.transactionId || values.transactionId.trim() === '') {
            return;
        }

        executePost({
            data: {
                id: id,
                providerType: providerType.toUpperCase(),
                reserved1: values.transactionId.trim(),
                providerName: getCheckoutInfoData.defaultChannel,
                customerAccount: getCheckoutInfoData.reserved1
            }
        }).then(() => {
            const currentTime = new Date().toLocaleString();
            setSubmitTime(currentTime);
            localStorage.setItem(SUBMIT_TIME_KEY, currentTime);
        })
        .catch((error) => {
            setShowFailureModal(true);
            setSubmitCheckout(false);
            messageApi.error("Failed:" + error?.response?.data?.title);
        });
    };

    const { loading: channelLoading, executePost: executeChannelPost } = usePostCheckoutChannelApi(id);

    // Cache provider type and receiver account when they change
    const updateProviderType = (type: string, account: string) => {
        setProviderType(type);
        setReceiveAccount(account);
        if (type && account) {
            const expiryTime = new Date().getTime() + CACHE_DURATION;
            localStorage.setItem(PROVIDER_STORAGE_KEY, type);
            localStorage.setItem(RECEIVER_ACCOUNT_KEY, account);
            localStorage.setItem(EXPIRY_STORAGE_KEY, expiryTime.toString());
            localStorage.setItem(ORDER_ID_KEY, id);
        }
    };

    const fetchChannelInfo = (values: string) => {
        setMethodError(false);
        setTransactionId('');
        setAllowSubmit(false);
        // 清除之前的數據
        setReceiveAccount('...');
        setSelectedChannel(values.toUpperCase());
        executeChannelPost({
            data: {
                providerType: values
            }
        }).then((res) => {
            if (res.data?.receiverAccount) {
                updateProviderType(values.toUpperCase(), res.data.receiverAccount);
            }
        }).catch((error) => {
            setSelectedChannel("");
            updateProviderType(getCachedProviderType(), getCachedReceiverAccount());
            messageApi.error("Failed:" + error?.response?.data?.title);
        })
    }

    const handleTransactionIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTransactionId(e.target.value);
        setAllowSubmit(true)
        setTransactionError(false);  // Clear error when typing
        if (!e.target.value) {
            setAllowSubmit(false);
        }
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        const stoppingStatuses: string[] = ["SUCCESS", "FAIL"];

        // Timer logic
        if (expiryTime) {
            const updateTimer = () => {
                const deadline = typeof expiryTime === 'string' ? parseInt(expiryTime) : expiryTime;
                const now = Date.now();
                const total = Math.max(0, deadline - now);

                if (total <= 0) {
                    console.log("[ExpiryTimer] timer expired");
                    setIsExpired(true);
                    clearInterval(timer);
                    setTimeLeft({ hours: '00', minutes: '00', seconds: '00' });
                    return;
                }

                const hours = String(Math.floor((total / (1000 * 60 * 60)) % 24)).padStart(2, '0');
                const minutes = String(Math.floor((total / (1000 * 60)) % 60)).padStart(2, '0');
                const seconds = String(Math.floor((total / 1000) % 60)).padStart(2, '0');

                setTimeLeft({ hours, minutes, seconds });
            };

            updateTimer();
            timer = setInterval(updateTimer, 1000);
        }

        // Polling logic
        if (isExpired && !submitCheckout) {
            console.log("[Polling] skip due to expired order");
        } else if (!submitCheckout || isPollingStopped) {
            console.log("[Polling] skip due to submitCheckout:", submitCheckout, "isPollingStopped:", isPollingStopped);
        } else {
            console.log("[Polling] start polling, loading:", pollingLoading);
            if ((getCheckoutResultData?.status === "PROCESSING" || getCheckoutResultData?.submitStatus === "PROCESSING") && !pollingTimeout) {
                console.log("[Polling] setup new polling interval");

                const poll = () => {
                    pollingCheckoutResult()
                        .then((data) => {
                            console.log("[Polling] received data:", data);
                            if (data?.data?.submitStatus && stoppingStatuses.includes(data.data.submitStatus)) {
                                console.log("[Polling] final status received:", data.data.submitStatus);
                                if (pollingTimeout) {
                                    clearTimeout(pollingTimeout);
                                    setPollingTimeout(null);
                                }
                                if (data.data.submitStatus === "FAIL") {
                                    console.log("[Polling] fail, pause polling");
                                    setSubmitCheckout(false);
                                    setShowFailureModal(true);
                                } else {
                                    console.log("[Polling] success, stop polling completely");
                                    setIsPollingStopped(true);
                                    setShowResult(true);
                                }
                                return;
                            }
                            
                            if ((data?.data?.status === "PROCESSING" || data?.data?.submitStatus === "PROCESSING") && !isPollingStopped) {
                                console.log("[Polling] continue polling, setup next interval");
                                const newTimeout = setTimeout(poll, PollingTime);
                                setPollingTimeout(newTimeout);
                            }
                        })
                        .catch((error) => {
                            console.log("[Polling] polling error:", error);
                            if (!isPollingStopped) {
                                console.log("[Polling] retry polling after error");
                                const newTimeout = setTimeout(poll, PollingTime);
                                setPollingTimeout(newTimeout);
                            }
                        });
                };

                const newTimeout = setTimeout(poll, PollingTime);
                setPollingTimeout(newTimeout);
            }
        }

        return () => {
            if (timer) {
                clearInterval(timer);
            }
            if (pollingTimeout) {
                clearTimeout(pollingTimeout);
                setPollingTimeout(null);
            }
        };
    }, [expiryTime, submitCheckout, pollingLoading, isExpired, pollingTimeout]);

    // 添加调试日志
    useEffect(() => {
        console.log("Current providerType:", providerType);
    }, [providerType]);

    return (
        <>
            {contextHolder}
            <Content>
                <div className="payment-container">
                    {!showResult || showFailureModal ? (
                        <div className="paymant-init-page">
                            {/* Header */}
                            <div className="payment-header">
                                <h2>Payable amount প্রদেয় পরিমাণ</h2>
                                <p className="payment-amount">
                                    <span style={{
                                        fontFamily: 'Noto Sans Bengali',
                                        marginRight: '4px',
                                        fontSize: '36px',
                                    }}>৳</span>
                                    {getCheckoutInfoData?.orderAmount}
                                </p>
                            </div>

                            {/* Countdown Timer */}
                            <div className="payment-content">
                                <div className="payment-timer-box">
                                    <span style={{ marginRight: '10px' }}>Time remaining অবশিষ্ট সময়</span>
                                    <span className="payment-timer">
                                        <span className="payment-timer-item">{timeLeft.hours}</span>:
                                        <span className="payment-timer-item">{timeLeft.minutes}</span>:
                                        <span className="payment-timer-item">{timeLeft.seconds}</span>
                                    </span>
                                </div>
                                {/* Step 1: Select Payment Method */}
                                <div className="payment-step-container">
                                    {providerType === "" && (
                                        <>
                                        <div className="payment-step">
                                            <div className="payment-step-title">
                                                <div className="payment-step-number" style={{background: '#FFFFFF'}}></div>
                                                <div className="payment-step-content">
                                                    <h3>Select your payment method</h3>
                                                    <h3 style={{color: '#808080'}}>আপনার পেমেন্ট পদ্ধতি নির্বাচন করুন</h3>
                                                </div>
                                            </div>
                                            <div className="payment-methods">
                                                {getCheckoutInfoData?.availableChannels?.map((channel: string) => (
                                                    <Button
                                                        key={channel}
                                                        className={`payment-method ${selectedChannel === channel.toUpperCase() ? "selected" : ""} ${methodError ? "error" : ""}`}
                                                        onClick={() => fetchChannelInfo(channel)}
                                                        disabled={isExpired || submitCheckout || channelLoading}
                                                    >
                                                        {selectedChannel === channel.toUpperCase() && (
                                                            <div className="payment-method-selected" />
                                                        )}
                                                        <img
                                                            className="payment-method-img"
                                                            src={logoMap[channel as keyof typeof logoMap]}
                                                            alt={channel}
                                                        />
                                                    </Button>
                                                ))}
                                                {methodError && (
                                                    <div className="error-text">Please select a payment method পেমেন্ট পদ্ধতি নির্বাচন করুন</div>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '90%',
                                            height: '1px',
                                            backgroundColor: '#e8e8e8',
                                            margin: '10px auto -8px auto'
                                        }} />
                                        </>
                                    )}

                                    {/* Step 2: Copy Wallet Details */}
                                    {providerType !== "" && providerType.length > 0 && (
                                        <>
                                            <div className="payment-step">
                                                <div className="payment-step-title">
                                                    <div className="payment-step-number">1</div>
                                                    <div className="payment-step-content">
                                                        {getCheckoutInfoData.reserved2 == "Agent" ? (
                                                            <>
                                                                <h3>Copy the wallet details and <span className="cash-out-text">Cash Out</span> the money to the wallet account.</h3>
                                                                <h3 style={{ color: '#808080' }}>ওয়ালেটের বিস্তারিত কপি করুন এবং টাকা ওয়ালেট অ্যাকাউন্টে ক্যাশ আউট করুন।</h3>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <h3>Copy the wallet details and <span className="cash-out-text">Make Payment</span> to the wallet account.</h3>
                                                                <h3 style={{ color: '#808080' }}>ওয়ালেটের বিস্তারিত কপি করুন এবং ওয়ালেট অ্যাকাউন্টে পেমেন্ট করুন।</h3>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="payment-wallet-box">
                                                    <span className="payment-wallet-number">{receiverAccount || getCheckoutInfoData.receiverAccount}</span>
                                                    <button
                                                        className="payment-copy-btn"
                                                        onClick={() => navigator.clipboard.writeText(receiverAccount || getCheckoutInfoData.receiverAccount)}
                                                    >
                                                        <img className="copy-icon" src={copy} alt="copy"/>
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{
                                                width: '90%',
                                                height: '1px',
                                                backgroundColor: '#e8e8e8',
                                                margin: '10px auto -8px auto'
                                            }} />

                                            {/* Step 3: Enter Transaction ID */}
                                            <div className="payment-step">
                                                <div className="payment-step-title">
                                                    <div className="payment-step-number">2</div>
                                                    <div className="payment-step-content">
                                                        <h3>Please enter the transaction ID and make sure you enter correctly.</h3>
                                                        <h3 style={{color: '#808080'}}> দয়া করে লেনদেন আইডি প্রবেশ করুন এবং নিশ্চিত করুন যে আপনি সঠিকভাবে প্রবেশ করেছেন।</h3>
                                                    </div>
                                                </div>
                                                <div className="payment-input-title">
                                                    <span style={{color: "#013198"}}>Transaction ID</span>
                                                    <span style={{color: "#99B9FF"}}>লেনদেন আইডি</span>
                                                </div>
                                                <input
                                                    type="text"
                                                    className={`payment-transaction-input ${transactionError ? "error" : ""}`}
                                                    placeholder="Enter the transaction ID লেনদেন আইডি প্রবেশ করুন"
                                                    value={transactionId}
                                                    disabled={providerType === ""}
                                                    onChange={handleTransactionIdChange}
                                                />
                                                {transactionError && (
                                                    <div className="error-text">Please enter transaction ID লেনদেন আইডি প্রবেশ করুন</div>
                                                )}
                                            </div>
                                            <Button
                                                className="payment-submit-btn"
                                                loading={loading || (submitCheckout && !showResult && !showFailureModal)} disabled={isExpired || submitCheckout || !allowSubmit}
                                                onClick={() => onFormFinish({ transactionId })}
                                            >
                                                {isExpired ? "Expired সময়সীমা শেষ" : "Submit জমা দিন"}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="payment-result-page">
                            <div className="payment-success-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M20 6L9 17L4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="payment-success-title">Payment Successful</div>
                            <div className="payment-success-subtitle">পেমেন্ট সফল হয়েছে</div>
                            <div className="payment-success-amount">
                                <span className="currency">৳</span>
                                {getCheckoutInfoData?.orderAmount}
                            </div>
                            <div className="payment-info-box">
                                <div className="payment-info-label">Order number মূলিকের আইডি নম্বর</div>
                                <div className="payment-info-value">{getCheckoutInfoData.merchantOrderId}</div>
                            </div>
                            <div className="payment-info-box">
                                <div className="payment-info-label">Payment time পেমেন্টের সময়</div>
                                <div className="payment-info-value">
                                    {submitTime || '-'}
                                </div>
                            </div>
                            <button className="return-button">Return ফিরে যান</button>
                        </div>
                    )}
                </div>
            </Content>

            {showFailureModal && (
                <div className="payment-modal-overlay">
                    <div className="payment-modal">
                        <div className="payment-modal-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div className="payment-modal-title">Submission Failed</div>
                        <div className="payment-modal-subtitle">জমা দেওয়া ব্যর্থ হয়েছে</div>
                        <div className="payment-modal-subtitle">
                            Please check again later<br/>
                            and try this transaction again.<br/>
                            দয়া করে পরে আবার চেক করুন এবং এই<br/>
                            লেনদেনটি আবার চেষ্টা করুন।
                        </div>
                        <button 
                            className="payment-modal-button"
                            onClick={() => {
                                setShowFailureModal(false);
                                setSubmitCheckout(false);
                            }}
                        >
                            Try again আবার চেষ্টা করুন
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default BdWalletMode;
